import { hasPermissions } from './permissionsHelper';
import { getDisabledRoutesForPhoenixAccount } from './phoenixHelper';

export const ROUTE_ACCESSIBILITY = {
  CHATLYN: 'chatlyn',
  PHOENIX: 'phoenix',
};

// eslint-disable-next-line default-param-last
export const getCurrentAccount = ({ accounts } = {}, accountId) => {
  return accounts.find((account) => account.id === accountId);
};

const getDisabledRoutesForAccountType = (
  accountAccessibility,
  isSuperAdmin
) => {
  switch (accountAccessibility) {
    case ROUTE_ACCESSIBILITY.PHOENIX: {
      return getDisabledRoutesForPhoenixAccount(isSuperAdmin);
    }

    case ROUTE_ACCESSIBILITY.CHATLYN: {
      return [];
    }

    default: {
      return [];
    }
  }
};

export const routeIsAccessibleFor = (route, userPermissions = []) => {
  const { meta: { permissions: routePermissions = [] } = {} } = route;
  return hasPermissions(routePermissions, userPermissions);
};

const routeIsAccessibleForAccount = (to, user, account) => {
  const isPhoenix = account?.custom_attributes?.featureSet === 'PHOENIX';
  const isSuperAdmin = user.type === 'SuperAdmin';

  const accountAccessibility = isPhoenix
    ? ROUTE_ACCESSIBILITY.PHOENIX
    : ROUTE_ACCESSIBILITY.CHATLYN;

  const routeAccessibility = to.meta?.onlyFor;

  const disabledRoutes = getDisabledRoutesForAccountType(
    accountAccessibility,
    isSuperAdmin
  );

  if (disabledRoutes.includes(to.name)) {
    return false;
  }

  if (!routeAccessibility) {
    return true;
  }

  return routeAccessibility === accountAccessibility;
};

const validateActiveAccountRoutes = (to, user, account) => {
  const accountDashboardURL = `accounts/${to.params.accountId}/dashboard`;

  // If the user is trying to access suspended route or account data isn't fetched, redirect them to dashboard
  if (to.name === 'account_suspended') {
    return accountDashboardURL;
  }

  // Check if the route is accessible for the user based on their permissions and type (e.g., SuperAdmin, Admin, Agent)
  const isAccessibleForUser =
    routeIsAccessibleFor(to, user.permissions) ||
    routeIsAccessibleFor(to, [user.type]);

  // Check if the route is accessible for the account based on its type (e.g., Chatlyn, Phoenix)
  const isAccessibleForAccount = routeIsAccessibleForAccount(to, user, account);

  // If the route is accessible, proceed
  if (isAccessibleForUser && isAccessibleForAccount) {
    return null;
  }

  // Redirect to fallback route if blocked by account type (not user permissions)
  const { fallbackRoute } = to.meta || {};
  if (!isAccessibleForAccount && isAccessibleForUser && fallbackRoute) {
    return `accounts/${to.params.accountId}/${fallbackRoute}`;
  }

  // Otherwise redirect to dashboard
  return accountDashboardURL;
};

export const validateLoggedInRoutes = (to, user) => {
  const currentAccount = getCurrentAccount(user, Number(to.params.accountId));
  // If current account is missing, either user does not have
  // access to the account or the account is deleted, return to login screen
  if (!currentAccount) {
    return `app/login`;
  }

  const isCurrentAccountActive = currentAccount.status === 'active';

  if (isCurrentAccountActive) {
    return validateActiveAccountRoutes(to, user, currentAccount);
  }

  // If the current account is not active, then redirect the user to the suspended screen
  if (to.name !== 'account_suspended') {
    return `accounts/${to.params.accountId}/suspended`;
  }

  // Proceed to the route if none of the above conditions are met
  return null;
};

export const isAConversationRoute = (
  routeName,
  includeBase = false,
  includeExtended = true
) => {
  const baseRoutes = [
    'home',
    'conversation_mentions',
    'conversation_unattended',
    'inbox_dashboard',
    'label_conversations',
    'team_conversations',
    'folder_conversations',
    'conversation_participating',
  ];
  const extendedRoutes = [
    'inbox_conversation',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_inbox',
    'conversations_through_label',
    'conversations_through_team',
    'conversations_through_folders',
    'conversation_through_participating',
  ];

  const routes = [
    ...(includeBase ? baseRoutes : []),
    ...(includeExtended ? extendedRoutes : []),
  ];

  return routes.includes(routeName);
};

export const getConversationDashboardRoute = (routeName) => {
  switch (routeName) {
    case 'inbox_conversation':
      return 'home';
    case 'conversation_through_mentions':
      return 'conversation_mentions';
    case 'conversation_through_unattended':
      return 'conversation_unattended';
    case 'conversations_through_label':
      return 'label_conversations';
    case 'conversations_through_team':
      return 'team_conversations';
    case 'conversations_through_folders':
      return 'folder_conversations';
    case 'conversation_through_participating':
      return 'conversation_participating';
    case 'conversation_through_inbox':
      return 'inbox_dashboard';
    default:
      return null;
  }
};

export const isAInboxViewRoute = (routeName, includeBase = false) => {
  const baseRoutes = ['inbox_view'];
  const extendedRoutes = ['inbox_view_conversation'];
  const routeNames = includeBase
    ? [...baseRoutes, ...extendedRoutes]
    : extendedRoutes;
  return routeNames.includes(routeName);
};

export const isNotificationRoute = (routeName) =>
  routeName === 'notifications_index';
